import { Dispatch, SetStateAction } from 'react';

import { EIMSClientConfig } from './auth.types';
import { Environment } from './env.types';

export const SupportedApps = {
  TtbApprover: 'ttb-approver',
  AppStart: 'app-start',
  GP: 'gp',
  HrHire: 'hr-hire',
  HrPerformance: 'hr-performance',
  HrAnalytics: 'hr-analytics',
  HrWorkerImport: 'hr-worker-import',
  HrTeamManager: 'hr-team-manager',
  HrTraining: 'hr-training',
  HrOnboarding: 'hr-onboarding',
  HrWorkerPosition: 'hr-worker-position',
  HrDocuments: 'hr-documents',
  HrWorkerList: 'hr-worker-list',
  NotFound: 'not-found',
  DawnDemo: 'dawn-demo',
  Embeddable: 'embeddable',
  Connect: 'hr-connect',
} as const;
export type SupportedApps = (typeof SupportedApps)[keyof typeof SupportedApps];

export type XemploInternalSettings = {
  /**
   * It determines the application baseUrl.
   * Typically used when routing rules uses url segments instead
   * of sub-domains. It defaults to `/`.
   */
  baseUrl?: string;

  /**
   * Internal app global state
   * DO NOT SET THIS VALUE ON APP BOOTSTRAP
   */
  dsPersonaId?: string;

  /**
   * Internal app global state
   * DO NOT SET THIS VALUE ON APP BOOTSTRAP
   */
  dsCompanyId?: string;

  /**
   * Internal app global state
   * DO NOT SET THIS VALUE ON APP BOOTSTRAP
   */
  dsSessionId?: string;

  /**
   * Internal app global state
   * DO NOT SET THIS VALUE ON APP BOOTSTRAP
   */
  setDsPersonaId: Dispatch<SetStateAction<string | null>>;

  /**
   * Internal app global state
   * DO NOT SET THIS VALUE ON APP BOOTSTRAP
   */
  setDsCompanyId: Dispatch<SetStateAction<string | null>>;

  /**
   * Internal app global state
   * DO NOT SET THIS VALUE ON APP BOOTSTRAP
   */
  setDsSessionId: Dispatch<SetStateAction<string | null>>;

  /**
   * Base URL of the selected instance's HR module, resolved during the select
   * persona flow via getHrModuleUrl. Unset when the instance has no HR module
   * (e.g. a GP-only persona) — never populate it from a persona's defaultUrl,
   * which is the persona's landing app and may be a non-HR module.
   *
   * Seeded from the session record by AppBootstrap; do not set it anywhere else.
   */
  hrInstanceUrl?: string;

  /**
   * Internal app global state
   * DO NOT SET THIS VALUE ON APP BOOTSTRAP
   */
  setHrInstanceUrl: Dispatch<SetStateAction<string | null>>;

  resetPersona: () => void;

  /**
   * Handles checking for instance url and creating a new URL
   */
  makeHrUrl: (
    endpoint: string,
    params?: URLSearchParams | Record<string, string>,
  ) => string;

  /**
   * Represents the same value obtained by VITE_EIMS_URL
   * with normalized name to be easier to consume within the app
   */
  eimsUrl: string;

  /**
   * Represents the same value obtained by VITE_DS_URL
   * with normalized name to be easier to consume within the app
   */
  dsUrl: string;

  /**
   * Represents the same value obtained by VITE_RELEASE_CHANNEL
   * with normalized name to be easier to consume within the app
   */
  releaseChannel: Environment;

  /**
   * Represents the same value obtained by VITE_APP_NAME
   * with normalized name to be easier to consume within the app
   */
  appName: SupportedApps;

  /**
   * Represents the same value obtained by VITE_CLOUD_ROLE
   * with normalized name to be easier to consume within the app
   */
  cloudRole: string;

  /**
   * Represents the same value obtained by VITE_INSTRUMENTATION_KEY
   * with normalized name to be easier to consume within the app
   *
   * @deprecated we are not going to use App Insights for FE anymore
   */
  instrumentationKey?: string;

  /**
   * Represents the same value obtained by VITE_SENTRY_DSN
   * with normalized name to be easier to consume within the app
   */
  sentryDsn?: string;

  /**
   * Some apps like GP have an independent API URL.
   * This is used to set the API URL for the app.
   */
  applicationApiUrl?: string;

  /**
   * This value comes from the AzureDevOps lib on each app deployment.
   * It will be used to navigate to persona selection screen as fallback.
   */
  startAppUrl: string;

  /** Some apps have a separate base url for subscribing to signalR hubs */
  signalRUrl?: string;

  /** Certain apps won't have a home as base url. Use this to define a custom default route */
  defaultRoute?: string;

  /** Internal flag that indicates if a token handover has occurred */
  hasTokenHandover: boolean;

  /** Function to set the token handover state */
  setHasTokenHandover: Dispatch<SetStateAction<boolean>>;

  /** Key used to store returnUrl in session storage */
  contextReturnUrlKey: string;

  /**
   * This is the callback that is called when the user logs out.
   * It is used to clean up the app state.
   */
  logoutCallback?: () => void;

  /**
   * Set by the app bootstrap when the session's persona belongs to another HR
   * instance; read by `withSelectedPersona` to show the no-access screen.
   */
  stalePersonaId?: string;
};

export type XemploCISettings = {
  VITE_DS_URL: string;
  VITE_EIMS_URL: string;
  VITE_GP_API_URL?: string;
  VITE_PAY_API_URL?: string;
  VITE_START_APP_URL?: string;
  VITE_SIGNALR_URL?: string;
  VITE_RELEASE_CHANNEL: Environment;
  /**
   * This used to represent the single instrumentation key.
   * Apparently, app insights is deprecating this form.
   * The correct approach is to use a connection string.
   *
   * Instead of changing this key for every app pipeline,
   * we will keep this lean and just update the value to
   * be the connection string.
   * Long term strategy, we will need to migrate the app
   * settings to something more robust like Zustand.
   */
  VITE_INSTRUMENTATION_KEY?: string;
  VITE_APP_NAME: SupportedApps;
  VITE_CLOUD_ROLE: string;
  VITE_SENTRY_DSN: string;
  EIMS_CLIENT_CONFIG: EIMSClientConfig;
  APP_CLIENT_ID: string;
  VITE_TRACKING_KEY?: string;
};

export type XemploAppSettings = XemploInternalSettings & XemploCISettings;

export const TimeFrameType = {
  Weekly: 1,
  Fortnightly: 2,
  Monthly: 3,
} as const;

export type TimeFrameType = (typeof TimeFrameType)[keyof typeof TimeFrameType];
